import React, { useContext, useEffect, useState, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from "react-router-dom";
import {
    Button,
    FloatButton,
    Table,
    TableProps,
    Tag,
    Typography,
    Card,
    Space,
    Tooltip,
    message,
    Modal,
    Input,
    Dropdown,
    Spin,
    Result,
    Progress,
    List,
    Statistic,
    Row,
    Col,
    Badge,
    Select,
    Switch,
    DatePicker
} from "antd";
import dayjs from 'dayjs';
import { AuthContext } from "../../../../config/AuthContext";
import { ColumnsType } from "antd/es/table";
import { BiArrowFromLeft, BiPlus, BiRefresh, BiCode } from "react-icons/bi";
import { DesktopOutlined, ExportOutlined, ReloadOutlined, PlayCircleOutlined, CodeOutlined, CheckCircleOutlined, FireOutlined, SearchOutlined, AreaChartOutlined, GiftOutlined, LockOutlined } from "@ant-design/icons";
import AppSider from "../../components/AppSider";
import UseWebSocketSubscription from "../../../UseWebSocketSubscription";
import { Content } from "antd/es/layout/layout";
import { GetUsuariIdJutgePractiques } from "../../../../dao/GetUsuariIdJutgePractiques";
import JutgeColors, { aulaColor as aulaColorFn } from "../../../../styles/JutgeColors";
import { FaEye, FaLaptopCode, FaUsers } from "react-icons/fa";
import { VscSettingsGear } from "react-icons/vsc";
import { IoDocumentTextOutline, IoOptions } from "react-icons/io5";
import AvaluacioColors from "../../../../styles/AvaluacioColors";
import { MdContentCopy, MdOutlineDriveFolderUpload } from "react-icons/md";
import ConfiguracioModal from "./ConfiguracioModal";
import ExercicisModal from "./ExercicisModal";
import CopyModal from "./CopyModal";
import PracticaIdTests from "../../practiquesId/content/PracticaIdTests";
import PracticaIdAlumnes from "../../practiquesId/content/PracticaIdAlumnes";
import DocumentModal from "./DocumentModal";
import { GetPracticaIdImagen } from "../../../../dao/GetPracticaIdImagen";
import UploadFolderModal from "./UploadFolderModal";
import { ReorderActivitats } from "../../../../dao/jutge/ReorderActivitats";
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MenuOutlined, ArrowUpOutlined, ArrowDownOutlined, MoreOutlined, CaretDownOutlined } from '@ant-design/icons';
import { BsLaptop } from 'react-icons/bs';
import { PostActivitat } from "../../../../dao/jutge/PostActivitat";
import { PostClaimReward } from "../../../../dao/jutge/PostClaimReward";
import { GetProblemaRecompenses } from "../../../../dao/jutge/GetProblemaRecompenses";
import { GetJavamonConfig } from "../../../../dao/jutge/GetJavamonConfig";
import { getJavamonImage } from "../../../../dao/jutge/GetJavamonConfig";
import RecompensaSummaryModal from "../../activitatsId/content/RecompensaSummaryModal";
import JavaMon from "../../problemesId/content/JavaMon";
import PremiosModal from "../../activitatsId/content/PremiosModal";
import { TrophyOutlined, ApartmentOutlined } from "@ant-design/icons";
import { GetUsuarisRecompensesProblema } from "../../../../dao/jutge/GetUsuarisRecompensesProblema";
import ModoDesafioTag from "../../activitatsId/content/ModoDesafioTag";
import AssignarRAsMassiuModal from "./AssignarRAsMassiuModal";

const { Text } = Typography;

interface PracticaImagenProps {
    id: number;
}

const PracticaImagen: React.FC<PracticaImagenProps> = ({ id }) => {
    const [imgSrc, setImgSrc] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;

        const fetchImage = () => {
            if (!mounted || imgSrc || document.hidden) return;
            GetPracticaIdImagen(id + "")
                .then((res: any) => {
                    if (mounted && res?.imagen) {
                        const raw = res.imagen;
                        let finalSrc = "";
                        if (typeof raw === "string") {
                            const trimmed = raw.trim();
                            if (trimmed === "avatar1.jpg" || trimmed === "/images/avatar1.jpg") {
                                finalSrc = "/images/avatar1.jpg";
                            } else if (trimmed.startsWith("data:") || trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("/")) {
                                finalSrc = trimmed;
                            } else if (trimmed.startsWith("base64:")) {
                                const b64 = trimmed.split(":").pop() || "";
                                finalSrc = `data:image/png;base64,${b64}`;
                            } else {
                                finalSrc = `data:image/png;base64,${trimmed}`;
                            }
                        }
                        setImgSrc(finalSrc || null);
                    }
                })
                .catch(() => { if (mounted) setImgSrc(null); });
        };

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchImage();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        fetchImage();

        return () => {
            mounted = false;
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [id, imgSrc]);

    if (!imgSrc) return <div style={{ width: 50, height: 50, background: '#eee', borderRadius: 12 }} />;

    return (
        <img
            src={imgSrc}
            alt="avatar"
            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
        />
    );
};

interface RowContextProps {
    setActivatorNodeRef?: (element: HTMLElement | null) => void;
    listeners?: any;
    attributes?: any;
}

const RowContext = React.createContext<RowContextProps>({});

interface DragRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    'data-row-key': string;
}

const TableDragRow = (props: DragRowProps) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: props['data-row-key'],
    });
    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };
    const contextValue = useMemo(
        () => ({ setActivatorNodeRef, listeners, attributes }),
        [setActivatorNodeRef, listeners, attributes]
    );

    return (
        <RowContext.Provider value={contextValue}>
            <tr {...props} ref={setNodeRef} style={style} />
        </RowContext.Provider>
    );
};


const RecompensesTagsPractica: React.FC<{ id_practica: string; isUnlocked: boolean; ele: any; refreshKey?: number; modoDesafio?: boolean; }> = ({ id_practica, isUnlocked, ele, refreshKey = 0, modoDesafio = false }) => {
    const { user } = useContext(AuthContext);
    const isAlumne = user?.tipus === "2";
    const isAdminOrProf = user?.tipus === "0" || user?.tipus === "1";
    const isCompleted = ele?.nota >= 50;
    const [recs, setRecs] = useState<any[]>([]);
    const [usuaris, setUsuaris] = useState<any[]>([]);

    useEffect(() => {
        if (!isUnlocked) return;
        let mounted = true;
        GetProblemaRecompenses(id_practica, undefined, 'PRACTICA').then(res => {
            if (mounted && res && Array.isArray(res)) setRecs(res);
        }).catch(() => { });
        GetUsuarisRecompensesProblema(id_practica, undefined, 'PRACTICA').then(res => {
            if (mounted && res && Array.isArray(res)) setUsuaris(res);
        }).catch(() => { });
        return () => { mounted = false; };
    }, [id_practica, isUnlocked, refreshKey]);

    if (!isUnlocked || !recs || recs.length === 0) return null;

    // Los alumnos solo pueden ver las recompensas si completaron la práctica (nota >= 50) y está en modo desafío
    if (isAlumne && (!modoDesafio || !isCompleted)) {
        return null;
    }

    const visibleItems = recs.map((r: any) => {
        let isExhausted = false;
        let exhaustionReason = '';
        let whoGotIt = '';
        let isClaimedByMe = false;

        const gotItUsers = usuaris.filter(u => {
            try {
                const uRewards = typeof u.rewards_json === 'string' ? JSON.parse(u.rewards_json) : u.rewards_json;
                return uRewards && Array.isArray(uRewards) && uRewards.some((ur: any) => ur.id === r.id);
            } catch (e) {
                return false;
            }
        });

        if (r.limit_quantity && r.limit_quantity > 0 && r.claimed_count >= r.limit_quantity) {
            isExhausted = true;
            exhaustionReason = 'Agotada';
            if (gotItUsers.length > 0) {
                whoGotIt = gotItUsers.map(u => u.nom).join(', ');
                exhaustionReason += ` (${whoGotIt})`;
            }
        }

        isClaimedByMe = gotItUsers.some(u => String(u.id) === String((user as any)?.id || (user as any)?.idUsuari));

        if (isAlumne && !isCompleted && !isExhausted && !isClaimedByMe) {
            return null;
        }

        let text = r.tipo === 'JAVAMON_RANDOM' ? 'Javamon Aleatori' : r.tipo;
        if (r.valor && r.valor > 1) text += ` x${r.valor}`;

        const hasImage = r.imagen && r.imagen.length > 0;
        let imgSrc = '';
        if (hasImage) {
            imgSrc = r.imagen === 'avatar1.jpg' ? '/images/avatar1.jpg' : (r.imagen.startsWith('data:') || r.imagen.startsWith('http') || r.imagen.startsWith('/') ? r.imagen : `data:image/png;base64,${r.imagen}`);
        } else if (r.id_item && !['PUNTUACION', 'JAVAMON_RANDOM'].includes(r.tipo)) {
            const apiUrl = process.env.REACT_APP_API_URL;
            let typeSegment = r.tipo.toLowerCase();
            if (typeSegment === "escudo") typeSegment = "escut";
            if (typeSegment === "arco") typeSegment = "arc";
            if (typeSegment === "shop_item") typeSegment = "shop";
            imgSrc = `${apiUrl}:8061/jutge/game/image/${typeSegment}/${r.id_item}`;
        }

        let content;
        let bgColor = "transparent";
        let borderColor = "gray";

        if (r.tipo === 'PUNTUACION') {
            content = <span style={{ color: 'gold', fontWeight: 'bold' }}>🪙 +{r.valor}</span>;
            bgColor = "black";
            borderColor = "#333";
        } else if (r.tipo === 'JAVAMON_RANDOM') {
            content = <span>⚔️</span>;
            bgColor = "#fff";
            borderColor = "#d9363e";
        } else if (imgSrc) {
            content = <img src={imgSrc} alt={r.tipo} style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: r.tipo.includes('JAVAMON') ? '50%' : '0' }} />;
        } else {
            content = <span>🎁</span>;
        }

        return {
            ...r,
            renderedContent: content,
            tooltipTitle: isExhausted ? exhaustionReason : text,
            isExhausted,
            exhaustionReason,
            bgColor,
            borderColor,
            grupoNum: Number(r.grupo_seleccion) || 0
        };
    }).filter(Boolean);

    // Agrupar recompensas del mismo grupo de selección (grupoNum > 0)
    const groupedElements: React.ReactNode[] = [];
    const processedGroupIds = new Set<number>();

    visibleItems.forEach((item: any, idx: number) => {
        if (item.grupoNum > 0) {
            if (processedGroupIds.has(item.grupoNum)) return;
            processedGroupIds.add(item.grupoNum);

            const groupItems = visibleItems.filter((x: any) => x.grupoNum === item.grupoNum);
            const allExhausted = groupItems.every((x: any) => x.isExhausted);
            const groupTooltip = (
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>🎯 Recompensa unificada (Tria 1 del Grup {item.grupoNum}):</div>
                    {groupItems.map((gi: any, gIdx: number) => (
                        <div key={gi.id || gIdx}>• {gi.tooltipTitle}</div>
                    ))}
                </div>
            );

            groupedElements.push(
                <Tooltip key={`group-${item.grupoNum}`} title={groupTooltip}>
                    <Tag
                        style={{
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            padding: '0 6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            background: '#f0f5ff',
                            border: '1px solid #adc6ff',
                            cursor: 'help',
                            opacity: allExhausted ? 0.4 : 1,
                            filter: allExhausted ? 'grayscale(100%)' : 'none'
                        }}
                    >
                        <span style={{ fontSize: '10px', color: '#1d39c4', fontWeight: 600, marginRight: 2 }}>
                            G{item.grupoNum}:
                        </span>
                        {groupItems.map((gi: any, gIdx: number) => (
                            <span 
                                key={gi.id || gIdx} 
                                style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center',
                                    opacity: gi.isExhausted ? 0.35 : 1,
                                    textDecoration: gi.isExhausted ? 'line-through' : 'none'
                                }}
                            >
                                {gi.renderedContent}
                            </span>
                        ))}
                    </Tag>
                </Tooltip>
            );
        } else {
            groupedElements.push(
                <Tooltip key={item.id || idx} title={item.tooltipTitle}>
                    <Tag
                        color={item.bgColor}
                        style={{
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            padding: '0 4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${item.borderColor}`,
                            cursor: 'help',
                            opacity: item.isExhausted ? 0.4 : 1,
                            textDecoration: item.isExhausted ? 'line-through' : 'none',
                            filter: item.isExhausted ? 'grayscale(100%)' : 'none'
                        }}
                    >
                        {item.renderedContent}
                    </Tag>
                </Tooltip>
            );
        }
    });

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
            {groupedElements}
        </div>
    );
};

interface PractiquesProps {
    onlyExams?: boolean;
}

const Practiques: React.FC<PractiquesProps> = ({ onlyExams = false }) => {
    const { isLoggedIn, userId, user, setBreadCrumb, setUser, alumne } = useContext(AuthContext);
    const navigate = useNavigate();
    const [dades, setDades] = useState<any[]>([]);
    const [recompensesRefreshKeys, setRecompensesRefreshKeys] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [modalE, setModalE] = useState(false);
    const [selected, setSelected] = useState<any>();
    const [pantalla, setPantalla] = useState(0);
    const [isExam, setIsExam] = useState(false);
    const [currentUrl, setCurrentUrl] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [password, setPassword] = useState("");
    const [duplicarModal, setDuplicarModal] = useState(false);
    const [documentModalVisible, setDocumentModalVisible] = useState(false);
    const [uploadFolderModalVisible, setUploadFolderModalVisible] = useState(false);

    // Modo Desafío state
    const [modoDesafio, setModoDesafio] = useState<boolean>((user as any)?.modoDesafio === 1 || (user as any)?.modoDesafio === true);

    // Exam Modal state
    const [ideStartModalVisible, setIdeStartModalVisible] = useState(false);
    const [ideStartLoading, setIdeStartLoading] = useState(false);
    const [ideStartUrl, setIdeStartUrl] = useState('');
    const [ideStartTheiaUrl, setIdeStartTheiaUrl] = useState(''); // Raw Theia URL (http://IP:PORT/...)
    const [ideStartExamEl, setIdeStartExamEl] = useState<any>(null);
    const [ideStartError, setIdeStartError] = useState<string | null>(null);

    // Reward state
    const [recompensaModalVisible, setRecompensaModalVisible] = useState(false);
    const [recompensaItems, setRecompensaItems] = useState<any[]>([]);
    const [selectableMode, setSelectableMode] = useState(false);
    const [selectionOptions, setSelectionOptions] = useState<any[]>([]);
    const [pendingSelectionItem, setPendingSelectionItem] = useState<any>(null);
    const [showBattle, setShowBattle] = useState(false);
    const [battleJavamon, setBattleJavamon] = useState<any>(null);
    const [pendingJavamonBattle, setPendingJavamonBattle] = useState<any>(null);
    const [activeRewardData, setActiveRewardData] = useState<any>(null);

    // Premios/Recompenses modal (admin)
    const [rankingsVisible, setRankingsVisible] = useState(false);
    const [selectedRecompensaPractica, setSelectedRecompensaPractica] = useState<any>(null);

    const [selectedPractica, setSelectedPractica] = useState<any>(null);
    const [modalViewVisible, setModalViewVisible] = useState(false);
    const [selectedPracticaView, setSelectedPracticaView] = useState<any>(null);

    const [alumnesModalVisible, setAlumnesModalVisible] = useState(false);
    const [alumnesData, setAlumnesData] = useState<any[]>([]);
    const [practicaAlumnesModalVisible, setPracticaAlumnesModalVisible] = useState(false);
    const [selectedPracticaForAlumnes, setSelectedPracticaForAlumnes] = useState<any>(null);

    // IDE Eval state
    const [ideEvalModalVisible, setIdeEvalModalVisible] = useState(false);
    const [ideEvalLoading, setIdeEvalLoading] = useState(false);
    const [ideEvalResult, setIdeEvalResult] = useState<any>(null);
    const [ideEvalPractica, setIdeEvalPractica] = useState<any>(null);
    const [ideEvalProgress, setIdeEvalProgress] = useState({ current: 0, total: 0, currentName: '' });
    const [isBulkEval, setIsBulkEval] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedAula, setSelectedAula] = useState<string | number>('all');

    // Selecció múltiple per accions massives (RAs)
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [modalAssignarRAs, setModalAssignarRAs] = useState(false);

    // Modal dedicat per a configurar el bloqueig / dependència
    const [modalBloqueoVisible, setModalBloqueoVisible] = useState(false);
    const [selectedPracticaBloqueo, setSelectedPracticaBloqueo] = useState<any>(null);
    const [selectedBloqueoId, setSelectedBloqueoId] = useState<number | undefined>(undefined);
    const [savingBloqueo, setSavingBloqueo] = useState(false);

    const handleOpenBloqueoModal = (practica: any) => {
        setSelectedPracticaBloqueo(practica);
        setSelectedBloqueoId(practica.id_bloqueo || undefined);
        setModalBloqueoVisible(true);
    };

    const handleSaveBloqueo = async () => {
        if (!selectedPracticaBloqueo) return;
        setSavingBloqueo(true);
        try {
            const payload = {
                id: selectedPracticaBloqueo.id,
                nom: selectedPracticaBloqueo.nom,
                estat: selectedPracticaBloqueo.estatr ?? selectedPracticaBloqueo.estat,
                id_aula: selectedPracticaBloqueo.id_aula,
                is_examen: selectedPracticaBloqueo.is_examen ? 1 : 0,
                is_concurs: selectedPracticaBloqueo.is_concurs ? 1 : 0,
                is_ide: selectedPracticaBloqueo.is_ide ? 1 : 0,
                context: selectedPracticaBloqueo.context || "",
                id_usuari: userId,
                tipus: selectedPracticaBloqueo.tipus_aula ?? selectedPracticaBloqueo.tipus ?? 0,
                id_season: selectedPracticaBloqueo.id_season || 1,
                inici: selectedPracticaBloqueo.inici ? dayjs(selectedPracticaBloqueo.inici.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null,
                fin: selectedPracticaBloqueo.final ? dayjs(selectedPracticaBloqueo.final.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null,
                javamons: selectedPracticaBloqueo.javamons ?? 10,
                minim: selectedPracticaBloqueo.minim ?? 0,
                escena: selectedPracticaBloqueo.escena || null,
                inst: "",
                id_bloqueo: selectedBloqueoId || null,
                ras: (selectedPracticaBloqueo.ras || []).map((r: any) => ({
                    id: r.id,
                    percentatge: r.percentatge || 0,
                    instrument: r.instrument
                }))
            };

            await PostActivitat(payload);
            message.success("Bloqueo de acceso actualizado correctamente");
            setModalBloqueoVisible(false);
            setSelectedPracticaBloqueo(null);
            await checkDades();
        } catch (err: any) {
            console.error("Error al actualizar el bloqueo:", err);
            message.error("Error al actualizar el bloqueo de acceso");
        } finally {
            setSavingBloqueo(false);
        }
    };

    // Llista d'aules úniques derivades de les dades de pràctiques
    const availableAulas = useMemo(() => {
        if (!Array.isArray(dades)) return [];
        const map = new Map<number | string, string>();
        dades.forEach((item: any) => {
            if (item.id_aula !== undefined && item.id_aula !== null) {
                if (!map.has(item.id_aula)) {
                    map.set(item.id_aula, item.aula || `Aula ${item.id_aula}`);
                }
            }
        });
        return Array.from(map.entries()).map(([id, nom]) => ({ id, nom }));
    }, [dades]);

    // Si només hi ha 1 aula, fixar-la automàticament; si n'hi ha més d'una, 'all' per defecte
    useEffect(() => {
        if (availableAulas.length === 1) {
            setSelectedAula(availableAulas[0].id);
        } else if (availableAulas.length > 1 && selectedAula !== 'all') {
            const exists = availableAulas.some(a => String(a.id) === String(selectedAula));
            if (!exists) setSelectedAula('all');
        }
    }, [availableAulas]);

    const filteredDades = useMemo(() => {
        if (!Array.isArray(dades)) return [];
        return dades.filter((p: any) => {
            // Filtre mode examen vs pràctica estàndard
            const isExamItem = Number(p.is_examen ?? p.isExamen) === 1;
            if (onlyExams && !isExamItem) return false;
            if (!onlyExams && isExamItem) return false;

            // Filtre d'aula: si selectedAula no és 'all', ha de coincidir
            if (selectedAula !== 'all' && String(p.id_aula) !== String(selectedAula)) {
                return false;
            }
            // Filtre de cerca
            if (searchText.trim()) {
                const q = searchText.toLowerCase();
                const matches = (p.nom && p.nom.toLowerCase().includes(q)) ||
                    (p.aula && p.aula.toLowerCase().includes(q)) ||
                    (p.descripcio && p.descripcio.toLowerCase().includes(q)) ||
                    (p.tags && p.tags.toLowerCase().includes(q)) ||
                    String(p.id).includes(q);
                if (!matches) return false;
            }
            return true;
        });
    }, [dades, selectedAula, searchText, onlyExams]);

    const stats = useMemo(() => {
        if (!Array.isArray(filteredDades) || filteredDades.length === 0) {
            return { total: 0, active: 0, exams: 0, completed: 0, avgNota: 0 };
        }
        let active = 0;
        let exams = 0;
        let completed = 0;
        let totalNota = 0;
        let countWithNota = 0;

        filteredDades.forEach((p: any) => {
            const isOcult = Number(p.estatr) === 5;
            const estat = isOcult ? 5 : Number(p.estat);
            if (estat === 3) active++;
            if (p.is_examen === 1) exams++;
            if (p.hasTests) {
                completed++;
                if (p.nota != null) {
                    totalNota += Number(p.nota);
                    countWithNota++;
                }
            }
        });

        return {
            total: filteredDades.length,
            active,
            exams,
            completed,
            avgNota: countWithNota > 0 ? (totalNota / countWithNota).toFixed(1) : "0"
        };
    }, [filteredDades]);

    const isAdmin = user.tipus === "0" || user.tipus === "1";

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 1 } })
    );

    const onDragEnd = ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id) {
            setDades((previous) => {
                const activeIndex = previous.findIndex((i) => i.id === active.id);
                const overIndex = previous.findIndex((i) => i.id === over?.id);
                const newDades = arrayMove(previous, activeIndex, overIndex);
                const ids = newDades.map(d => d.id + "");
                ReorderActivitats(ids).catch(() => {
                    message.error("Error al guardar el orden");
                    checkDades();
                });
                return newDades;
            });
        }
    };

    const moveUp = (index: number) => {
        if (index > 0) {
            const newDades = [...dades];
            [newDades[index], newDades[index - 1]] = [newDades[index - 1], newDades[index]];
            setDades(newDades);
            ReorderActivitats(newDades.map(d => d.id + ""));
        }
    };

    const moveDown = (index: number) => {
        if (index < dades.length - 1) {
            const newDades = [...dades];
            [newDades[index], newDades[index + 1]] = [newDades[index + 1], newDades[index]];
            setDades(newDades);
            ReorderActivitats(newDades.map(d => d.id + ""));
        }
    };

    useEffect(() => {
        if (isLoggedIn) {
            // Si el usuario es alumno y no estamos en la pestaña de exámenes (es decir, está en /jutge/practiques)
            // y su aula está en modo examen, se redirige hacia atrás.
            if (!onlyExams && user.tipus === "2") {
                const userAulas = alumne?.aules || [];
                const hasExamMode = (userAulas || []).some((a: any) =>
                    Boolean(a.isExamen === 1 || a.isExamen === true || a.is_examen === 1 || a.is_examen === true)
                );
                if (hasExamMode) {
                    navigate(-1);
                    return;
                }
            }

            setBreadCrumb([
                { nom: 'Home', nav: '/home' },
                { nom: 'jutge', nav: '/jutge' },
                ...(onlyExams ? [{ nom: 'exàmens', nav: '/jutge/examens' }] : [{ nom: 'practiques', nav: '/jutge/practiques' }])
            ], true);
            checkDades();

            const handleVisibilityChange = () => {
                if (document.hidden) {
                    setCurrentUrl(window.location.href);
                    if (isExam) setModalVisible(true);
                }
            };

            document.addEventListener("visibilitychange", handleVisibilityChange);

            return () => {
                document.removeEventListener("visibilitychange", handleVisibilityChange);
            };
        }
    }, []);

    UseWebSocketSubscription(
        ["/topic/home", ...(userId ? [`/topic/exam/reset/${userId}`] : [])],
        (topic: string, data: any) => {
            if (topic === "/topic/home" || (data && data.reset)) {
                checkDades();
            }
        }
    );

    const handlePasswordSubmit = () => {
        if (password === "1234") {
            setModalVisible(false);
            setPassword("");
            message.success("Password correcta, puedes continuar");
        } else {
            message.error("Password incorrecta");
        }
    };

    const startExamModal = (ele: any, useWrapper: boolean = true) => {
        setIdeStartExamEl(ele);
        setIdeStartModalVisible(true);
        setIdeStartLoading(true);
        setIdeStartUrl('');
        setIdeStartError(null);
        startExamProcess(ele, useWrapper);
    };

    const handleToggleModoDesafio = async (checked: boolean) => {
        try {
            await import('../../../../dao/PutModoDesafio').then(m => m.PutModoDesafio(userId, checked));
            setModoDesafio(checked);
            if (setUser) {
                setUser({ ...(user as any), modoDesafio: checked ? 1 : 0 });
            }
            message.success(`Modo Desafío ${checked ? 'Activado' : 'Desactivado'}`);
        } catch (e) {
            message.error("Error al actualizar el Modo Desafío");
        }
    };

    const startExamProcess = async (ele: any, useWrapper: boolean = true) => {
        setIdeStartLoading(true);
        setIdeStartUrl('');
        setIdeStartError(null);
        try {
            const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost') + ':8061';
            const token = localStorage.getItem('authToken');

            // Only mark as started if it's a student and it's an exam
            if (user.tipus === "2" && ele.is_examen === 1) {
                await fetch(`${baseUrl}/jutge/exam/${ele.id}/start`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ id_usuari: userId })
                }).catch(() => { /* ignore */ });
                checkDades(); // Refresh hidden start access
            }

            // Provision IDE — poll until ready, retrying on transient fetch/network errors too
            const folderName = ele.nom || "";
            let attempts = 0;
            const maxAttempts = 60; // 60 × 5s = 5 minutes max

            while (attempts < maxAttempts) {
                let startRes: Response;
                try {
                    const aulaQuery = (ele && ele.id_aula) ? `&id_aula=${ele.id_aula}` : '';
                    startRes = await fetch(`${baseUrl}/jutge/theia/port?id_usuari=${userId}&folder=${encodeURIComponent(folderName)}&id_practica=${ele.id}${aulaQuery}&admin=true`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                } catch (fetchErr) {
                    // Network / timeout error — Docker may still be starting; retry
                    attempts++;
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    continue;
                }

                if (!startRes.ok) {
                    // HTTP error from server — wait and retry instead of bailing out immediately
                    let errorMessage = `HTTP ${startRes.status}`;
                    try {
                        const errData = await startRes.json();
                        if (errData && errData.error) errorMessage = errData.error;
                    } catch (e) { }
                    attempts++;
                    if (attempts >= maxAttempts) throw new Error(errorMessage);
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    continue;
                }

                const data = await startRes.json();

                if (data.status === 'provisioning' || data.hostname === 'Pending...' || data.port === 0) {
                    attempts++;
                    await new Promise(resolve => setTimeout(resolve, 5000));
                    continue;
                }

                // Success! Construct URL
                const projectName = data.normalizedProjectName;
                let theiaBase = data.httpsUrl || `http://${data.hostname || window.location.hostname}:${data.port}`;
                if (theiaBase.endsWith("/")) theiaBase = theiaBase.slice(0, -1);

                let theiaUrl = projectName
                    ? `${theiaBase}/#/mnt/efs/${userId}/${projectName}`
                    : `${theiaBase}/#/mnt/efs/${userId}`;

                setIdeStartTheiaUrl(theiaUrl);

                if (useWrapper) {
                    const examWrapperUrl = `${window.location.origin}/ide/exam-v2?url=${encodeURIComponent(theiaUrl)}&idPractica=${ele.id}${modoDesafio ? '&isDesafio=true' : ''}`;
                    setIdeStartUrl(examWrapperUrl);
                } else {
                    setIdeStartUrl(theiaUrl);
                }

                setIdeStartLoading(false);
                return;
            }

            throw new Error("S'ha superat el temps d'espera per provisionar l'IDE.");
        } catch (e: any) {
            console.error(e);
            setIdeStartLoading(false);
            setIdeStartError(e.message || 'Error desconocido al preparar el entorno.');
        }
    };

    const handleClaimRewardClick = async (ele: any) => {
        if (ele.origen_success === 2) {
            message.warning("Esta recompensa solo se puede reclamar desde el juego (Unity).");
            return;
        }
        if (ele.origen_success !== 1) {
            message.warning("Solo se pueden reclamar recompensas si la práctica se ha completado en Modo Desafío.");
            return;
        }
        const id_practica = ele.id + "";
        try {
            message.loading({ content: "Revisando recompensas...", key: "claim" });
            const recompensas = await GetProblemaRecompenses(id_practica, undefined, 'PRACTICA');

            // Comprobar si hay recompensas con grupo_seleccion > 0 (elección entre varias)
            const groupedRewards = (recompensas || []).filter((r: any) => Number(r.grupo_seleccion) > 0);
            if (groupedRewards.length > 0) {
                message.destroy("claim");
                setSelectionOptions(groupedRewards);
                setPendingSelectionItem(ele);
                setSelectableMode(true);
                setRecompensaModalVisible(true);
                return;
            }

            const javamonRandom = recompensas?.find((r: any) => r.tipo === 'JAVAMON_RANDOM');
            const javamonSpecific = recompensas?.find((r: any) => r.tipo === 'JAVAMON');

            let triggerBattle = !!javamonRandom || !!javamonSpecific;

            if (triggerBattle) {
                const data = await GetJavamonConfig();
                if (data) {
                    const rarities = data.rarities && data.rarities !== "null" ? JSON.parse(data.rarities) : [];
                    const javamons = data.javamons && data.javamons !== "null" ? JSON.parse(data.javamons) : [];

                    if (rarities.length > 0 && javamons.length > 0) {
                        let jmChosen: any = null;
                        let rarityChosen: any = null;

                        if (javamonRandom) {
                            const rndRarity = Math.random() * 100;
                            rarityChosen = rarities.filter((r: any) => r.active)[0];
                            let accumR = 0;
                            for (let c of rarities.filter((r: any) => r.active)) {
                                accumR += c.chance;
                                if (rndRarity <= accumR) { rarityChosen = c; break; }
                            }
                            if (rarityChosen) {
                                let availableOfRarity = javamons.filter((jm: any) => jm.id_rarity === rarityChosen.id && jm.active);
                                if (availableOfRarity.length === 0) availableOfRarity = javamons.filter((jm: any) => jm.active);
                                if (availableOfRarity.length > 0) {
                                    const rndJavamon = Math.random() * 100;
                                    jmChosen = availableOfRarity[0];
                                    let accumJ = 0;
                                    for (let j of availableOfRarity) {
                                        accumJ += (j.chance || 0);
                                        if (rndJavamon <= accumJ) { jmChosen = j; break; }
                                    }
                                }
                            }
                        } else if (javamonSpecific) {
                            jmChosen = javamons.find((j: any) => j.id == javamonSpecific.id_item);
                            if (jmChosen) rarityChosen = rarities.find((r: any) => r.id === jmChosen.id_rarity);
                        }

                        if (jmChosen) {
                            // For practiques, we pass esPractica = true so the backend knows to evaluate it as a practice
                            const claimRes = await PostClaimReward(id_practica, id_practica, user.id + "", undefined, jmChosen.id + "", true);
                            if (!claimRes || claimRes.status !== 'ok') {
                                message.error({ content: claimRes?.message || "Error al reclamar la recompensa", key: "claim" });
                                return;
                            }
                            message.destroy("claim");
                            checkDades();
                            const previewRewards = recompensas?.map((r: any) => {
                                if (r.tipo === 'JAVAMON_RANDOM' || r.tipo === 'JAVAMON') return { tipo: 'JAVAMON', data: jmChosen };
                                return r;
                            }) || [];
                            setPendingJavamonBattle({
                                idProblema: id_practica,
                                javamon: { id: jmChosen.id, name: jmChosen.nom, image: getJavamonImage(jmChosen), rarity: rarityChosen?.nom || 'normal' }
                            });
                            setSelectableMode(false);
                            setRecompensaItems(previewRewards);
                            setRecompensaModalVisible(true);
                            return;
                        }
                    }
                }
            }

            const res = await PostClaimReward(id_practica, id_practica, user.id + "", undefined, undefined, true);
            if (res && res.status === 'ok') {
                message.success({ content: res.message || "Recompensa reclamada con éxito", key: "claim" });
                setSelectableMode(false);
                setRecompensaItems(res.claimed || []);
                setRecompensaModalVisible(true);
                checkDades();
            } else {
                message.error({ content: res?.message || "Error al reclamar la recompensa", key: "claim" });
            }
        } catch (e: any) {
            console.error("Error al reclamar recompensa:", e);
            message.error({ content: e?.message || "Error de red", key: "claim" });
        }
    };

    const handleConfirmSelection = async (chosenReward: any) => {
        if (!pendingSelectionItem) return;
        const id_practica = pendingSelectionItem.id + "";
        try {
            message.loading({ content: "Reclamando tu selección...", key: "claim" });
            const res = await PostClaimReward(id_practica, id_practica, user.id + "", undefined, undefined, true, chosenReward.id);
            if (res && res.status === 'ok') {
                message.success({ content: res.message || "¡Recompensa elegida y obtenida con éxito!", key: "claim" });
                setSelectableMode(false);
                setRecompensaItems(res.claimed || []);
                checkDades();
            } else {
                message.error({ content: res?.message || "Error al reclamar la recompensa seleccionada", key: "claim" });
            }
        } catch (e: any) {
            message.error({ content: e?.message || "Error al reclamar", key: "claim" });
        }
    };

    const onBattleSuccess = async () => {
        if (activeRewardData && battleJavamon) {
            const res = await PostClaimReward(activeRewardData.idActivitat, activeRewardData.idProblema, activeRewardData.userId, battleJavamon.id + "", undefined, true);
            if (res && res.status === 'ok') {
                message.success(res.message);
                checkDades();
            } else {
                message.error("Error al reclamar la recompensa tras captura");
            }
        }
    };

    async function closeModalRanking(refresh: boolean) {
        setRankingsVisible(false);
        if (refresh) {
            await checkDades();
        }
    }

    function rankings(ele: any) {
        setSelectedRecompensaPractica(ele);
        setRankingsVisible(true);
    }


    async function checkDades() {
        setLoading(true);
        try {
            const data = await GetUsuariIdJutgePractiques(userId);
            setDades(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setDades([]);
        } finally {
            setLoading(false);
        }
    }

    const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);

    async function handleQuickChangeEstat(record: any, newEstat: number) {
        if (updatingStatusId) return;
        setUpdatingStatusId(record.id);
        try {
            const payload = {
                id: record.id,
                nom: record.nom,
                estat: newEstat,
                id_aula: record.id_aula,
                is_examen: record.is_examen ? 1 : 0,
                is_concurs: record.is_concurs ? 1 : 0,
                is_ide: record.is_ide ? 1 : 0,
                context: record.context || "",
                id_usuari: userId,
                tipus: record.tipus_aula ?? record.tipus ?? 0,
                id_season: record.id_season || 1,
                inici: record.inici ? dayjs(record.inici.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null,
                fin: record.final ? dayjs(record.final.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null,
                javamons: record.javamons ?? 10,
                minim: record.minim ?? 0,
                escena: record.escena || null,
                inst: "",
                ras: (record.ras || []).map((r: any) => ({
                    id: r.id,
                    percentatge: r.percentatge || 0,
                    instrument: r.instrument
                }))
            };

            await PostActivitat(payload);
            message.success("Estat de la pràctica actualitzat");
            await checkDades();
        } catch (err: any) {
            console.error("Error al canviar l'estat:", err);
            message.error("Error al canviar l'estat");
        } finally {
            setUpdatingStatusId(null);
        }
    }

    const [updatingDateId, setUpdatingDateId] = useState<number | null>(null);

    async function handleQuickChangeDate(record: any, field: 'inici' | 'final', newDate: dayjs.Dayjs | null) {
        if (updatingDateId) return;
        setUpdatingDateId(record.id);
        try {
            const formattedDate = newDate ? newDate.format('YYYY-MM-DD HH:mm:ss') : null;
            const payload = {
                id: record.id,
                nom: record.nom,
                estat: record.estatr ?? record.estat,
                id_aula: record.id_aula,
                is_examen: record.is_examen ? 1 : 0,
                is_concurs: record.is_concurs ? 1 : 0,
                is_ide: record.is_ide ? 1 : 0,
                context: record.context || "",
                id_usuari: userId,
                tipus: record.tipus_aula ?? record.tipus ?? 0,
                id_season: record.id_season || 1,
                inici: field === 'inici' ? formattedDate : (record.inici ? dayjs(record.inici.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null),
                fin: field === 'final' ? formattedDate : (record.final ? dayjs(record.final.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null),
                javamons: record.javamons ?? 10,
                minim: record.minim ?? 0,
                escena: record.escena || null,
                inst: "",
                ras: (record.ras || []).map((r: any) => ({
                    id: r.id,
                    percentatge: r.percentatge || 0,
                    instrument: r.instrument
                }))
            };

            await PostActivitat(payload);
            message.success(`Data de ${field === 'inici' ? 'inici' : 'finalització'} actualitzada`);
            await checkDades();
        } catch (err: any) {
            console.error("Error al actualitzar la data:", err);
            message.error("Error al actualitzar la data");
        } finally {
            setUpdatingDateId(null);
        }
    }

    function showSettings(ele: any) {
        setSelected(ele);
        setModal(true);
    }

    function showExercicis(ele: any) {
        setSelected(ele);
        setModalE(true);
    }

    function onCloseModal(reload: boolean) {
        setModal(false);
        setModalE(false);
        if (reload) checkDades();
        else setLoading(false);
    }

    async function submitFromIde(ele: any, mode: 'single' | 'bulk' = 'single') {
        setIdeEvalPractica(ele);
        setIdeEvalResult(null);
        setIdeEvalLoading(true);
        setIdeEvalModalVisible(true);
        setIsBulkEval(mode === 'bulk');

        const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost') + ':8061';
        const token = localStorage.getItem('authToken');

        const doSingleSubmit = async (targetId: string, name: string) => {
            try {
                const response = await fetch(`${baseUrl}/jutge/ide/practica/submit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        idPractica: ele.id + '',
                        idUsuari: targetId + '',
                        folder: ele.nom
                    })
                });
                const text = await response.text();
                let data: any = null;
                try { data = JSON.parse(text); } catch (ex) { }

                if (!response.ok) return { error: data?.error || text, name, status: 'skipped' };
                return { ...data, name, status: 'ok' };
            } catch (e: any) {
                return { error: e.message || 'Error de xarxa', name, status: 'error' };
            }
        };

        if (mode === 'bulk') {
            try {
                const { GetUsuarisPractica } = await import("../../../../dao/jutge/GetUsuarisPractica");
                const allUsers = await GetUsuarisPractica(ele.id + "");

                // Filter out non-students (ignore role 0 and 1)
                const students = allUsers.filter((u: any) => u.tipus !== "0" && u.tipus !== "1");

                const results: any[] = [];
                const total = students.length;
                setIdeEvalProgress({ current: 0, total, currentName: 'Iniciant...' });
                setIdeEvalResult({ bulk: true, results: [] });

                for (let i = 0; i < total; i++) {
                    const s = students[i];
                    setIdeEvalProgress({ current: i + 1, total, currentName: `${s.nom ?? ''} ${s.cognoms ?? ''}` });
                    const res = await doSingleSubmit(s.id + '', `${s.nom ?? ''} ${s.cognoms ?? ''}`);
                    results.push(res);
                    // Update live results
                    setIdeEvalResult({ bulk: true, results: [...results] });
                }
            } catch (err: any) {
                setIdeEvalResult({ error: "Error obtenint llistat d'alumnes: " + err.message });
            } finally {
                setIdeEvalLoading(false);
            }
        } else {
            const res = await doSingleSubmit(userId + '', 'Admin');
            setIdeEvalResult(res);
            setIdeEvalLoading(false);
        }
    }

    const DragHandle: React.FC = () => {
        const { setActivatorNodeRef, listeners, attributes } = useContext(RowContext);
        return (
            <span
                ref={setActivatorNodeRef}
                {...listeners}
                {...attributes}
                style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}
            >
                <MenuOutlined style={{ color: '#999', pointerEvents: 'none' }} />
            </span>
        );
    };

    const baseColumns: ColumnsType<any> = [
        {
            key: 'drag-handle',
            width: 30,
            align: 'center' as const,
            hidden: !isAdmin,
            render: () => <DragHandle />,
        },
        {
            key: 'sort',
            title: 'Ordre',
            width: 80,
            align: 'center' as const,
            hidden: !isAdmin,
            render: (_: any, __: any, index: number) => (
                <Space direction="vertical" size={0}>
                    <Button type="text" size="small" icon={<ArrowUpOutlined />}
                        disabled={index === 0} onClick={() => moveUp(index)} />
                    <Button type="text" size="small" icon={<ArrowDownOutlined />}
                        disabled={index === dades.length - 1} onClick={() => moveDown(index)} />
                </Space>
            )
        },
        {
            key: 'imagen',
            dataIndex: 'imagen',
            width: 50,
            render: (_img: any, record: any) => <PracticaImagen id={record.id} />
        },
        {
            key: 'nom',
            dataIndex: 'nom',
            title: 'Nom',
            render: (text, ele) => {
                const aulaColor = aulaColorFn(ele.id_aula);
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text strong style={{ fontSize: '1rem' }}>{text}</Text>
                        <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                            <Tag color={aulaColor} style={{ color: '#fff', border: 'none' }}>{ele.aula ?? ele.id_aula}</Tag> ID: {ele.id}
                        </Text>
                        {((Array.isArray(ele.ras) && ele.ras.length > 0) || ele.tags) && (
                            <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                                {Array.isArray(ele.ras) && ele.ras.map((ra: any) => {
                                    const raColors = [
                                        'magenta', 'red', 'volcano', 'orange', 'gold',
                                        'lime', 'green', 'cyan', 'blue', 'geekblue'
                                    ];
                                    const raNum = parseInt(ra.nom.replace(/\D/g, ''), 10) || ra.id;
                                    const color = raColors[(raNum - 1) % raColors.length];
                                    const displayNom = ra.nom.toString().startsWith('RA') ? ra.nom : `RA${ra.nom}`;

                                    return (
                                        <Tag key={ra.id} color={color} style={{ fontSize: '0.75rem', margin: 0 }}>
                                            {displayNom} - {ra.instrument}
                                        </Tag>
                                    );
                                })}
                                {ele.tags && (
                                    (Array.isArray(ele.tags) ? ele.tags : String(ele.tags).split(/[\s,]+/))
                                        .map((t: string) => t.trim())
                                        .filter(Boolean)
                                        .map((tagStr: string, idx: number) => (
                                            <Tag
                                                key={`custom-tag-${idx}`}
                                                style={{
                                                    background: '#f0f5ff',
                                                    border: '1px solid #adc6ff',
                                                    color: '#1d39c4',
                                                    borderRadius: 12,
                                                    padding: '0 8px',
                                                    fontSize: '0.72rem',
                                                    fontWeight: 500,
                                                    margin: 0
                                                }}
                                            >
                                                #{tagStr}
                                            </Tag>
                                        ))
                                )}
                            </div>
                        )}
                    </div>
                );
            },
            filters: Array.from(new Map((Array.isArray(dades) ? dades : []).map(a => [a.id_aula, a.aula])).entries())
                .map(([id_aula, aula]) => ({ text: `${aula ?? id_aula}`, value: id_aula })),
            onFilter: (value, record) => record.id_aula === value,
            filterSearch: true
        },
        {
            key: 'dates',
            title: 'Dates',
            align: 'center',
            render: (_, record) => {
                const getFormattedDate = (dateStr: any) => {
                    if (!dateStr) return '-';
                    return dayjs(dateStr.toString().substring(0, 19)).format('DD/MM/YYYY HH:mm');
                };

                if (!isAdmin) {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                            <Tag color="green" style={{ margin: 0 }}>{getFormattedDate(record.inici)}</Tag>
                            <Tag color="red" style={{ margin: 0 }}>{getFormattedDate(record.final)}</Tag>
                        </div>
                    );
                }

                return (
                    <div
                        style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Tooltip title="Fes clic per modificar la data d'inici">
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: record.inici ? '#f6ffed' : 'transparent',
                                borderRadius: 4,
                                padding: '0 6px',
                                height: 22
                            }}>
                                <DatePicker
                                    size="small"
                                    showTime={{ format: 'HH:mm' }}
                                    format="DD/MM/YYYY HH:mm"
                                    value={record.inici ? dayjs(record.inici.toString().substring(0, 19)) : null}
                                    onChange={(date) => handleQuickChangeDate(record, 'inici', date)}
                                    allowClear
                                    placeholder="Sense inici"
                                    style={{
                                        padding: 0,
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        width: record.inici ? 'auto' : 85,
                                        fontSize: '0.75rem',
                                        color: record.inici ? '#389e0d' : undefined
                                    }}
                                />
                            </div>
                        </Tooltip>
                        <Tooltip title="Fes clic per modificar la data final">
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: record.final ? '#fff1f0' : 'transparent',
                                borderRadius: 4,
                                padding: '0 6px',
                                height: 22
                            }}>
                                <DatePicker
                                    size="small"
                                    showTime={{ format: 'HH:mm' }}
                                    format="DD/MM/YYYY HH:mm"
                                    value={record.final ? dayjs(record.final.toString().substring(0, 19)) : null}
                                    onChange={(date) => handleQuickChangeDate(record, 'final', date)}
                                    allowClear
                                    placeholder="Sense final"
                                    style={{
                                        padding: 0,
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        width: record.final ? 'auto' : 85,
                                        fontSize: '0.75rem',
                                        color: record.final ? '#cf1322' : undefined
                                    }}
                                />
                            </div>
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            key: 'estat',
            dataIndex: 'estat',
            title: 'Estat',
            align: 'center',
            filters: [
                { text: 'Actiu', value: 3 },
                { text: 'Tancat', value: 2 },
                { text: 'Revisió', value: 4 },
                { text: 'No visible', value: 1 },
                { text: 'Ocult', value: 5 },
            ],
            // Per defecte es mostren tots els estats excepte "Ocult" (5)
            defaultFilteredValue: ['1', '2', '3', '4'],
            // Fem servir l'estat REAL (estatr) per detectar "Ocult" (5), perquè el
            // camp 'estat' que retorna el backend està recalculat per dates.
            onFilter: (value, record) => {
                const real = Number(record.estatr);
                if (Number(value) === 5) return real === 5;
                if (real === 5) return false;
                return Number(record.estat) === Number(value);
            },
            render: (e, record) => {
                // Prioritza l'estat real "Ocult" (5) sobre l'estat calculat
                const isOcult = Number(record.estatr) === 5;
                const estat = isOcult ? 5 : e;
                const isCurrentUpdating = updatingStatusId === record.id;

                const tagElement = (
                    <Tag
                        color={
                            estat === 3 ? JutgeColors.colors.color3 :
                                estat === 2 ? JutgeColors.colors.color4 :
                                    estat === 4 ? JutgeColors.colors.color5 :
                                        estat === 5 ? "default" : "red"
                        }
                        style={{
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            margin: 0,
                            cursor: isAdmin ? 'pointer' : 'default',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            userSelect: 'none',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isCurrentUpdating ? (
                            <Spin size="small" style={{ marginRight: 4 }} />
                        ) : null}
                        {estat === 3 ? 'Actiu' : estat === 2 ? 'Tancat' : estat === 4 ? 'Revisió' : estat === 5 ? 'Ocult' : 'No visible'}
                        {isAdmin && !isCurrentUpdating && (
                            <CaretDownOutlined style={{ fontSize: '0.65rem', opacity: 0.7 }} />
                        )}
                    </Tag>
                );

                const statusMenu = isAdmin ? {
                    items: [
                        {
                            key: '3',
                            label: <Tag color={JutgeColors.colors.color3} style={{ fontWeight: 'bold' }}>Actiu</Tag>,
                            onClick: () => handleQuickChangeEstat(record, 3)
                        },
                        {
                            key: '2',
                            label: <Tag color={JutgeColors.colors.color4} style={{ fontWeight: 'bold' }}>Tancat</Tag>,
                            onClick: () => handleQuickChangeEstat(record, 2)
                        },
                        {
                            key: '1',
                            label: <Tag color="red" style={{ fontWeight: 'bold' }}>No visible</Tag>,
                            onClick: () => handleQuickChangeEstat(record, 1)
                        },
                        {
                            key: '5',
                            label: <Tag color="default" style={{ fontWeight: 'bold' }}>Ocult</Tag>,
                            onClick: () => handleQuickChangeEstat(record, 5)
                        }
                    ]
                } : undefined;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                        {isAdmin && statusMenu ? (
                            <Dropdown
                                menu={statusMenu}
                                trigger={['click']}
                                disabled={Boolean(updatingStatusId)}
                            >
                                <Tooltip title="Canviar estat">
                                    <span style={{ cursor: 'pointer' }}>
                                        {tagElement}
                                    </span>
                                </Tooltip>
                            </Dropdown>
                        ) : (
                            tagElement
                        )}
                        {!onlyExams && record.is_examen === 1 && (
                            <Tooltip title="Examen">
                                <Tag color="volcano" style={{ fontWeight: 'bold', fontSize: '0.85rem', margin: 0 }}>EX</Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'nota',
            title: (isAdmin || modoDesafio) ? 'Nota & Recompenses' : 'Nota',
            align: 'center',
            render: (text, record) => {
                // Exam grades hidden from students until exam closes (estat Tancat = 2)
                const isExamNotClosed = record.is_examen === 1 && Number(record.estat) !== 2;
                const hideNota = isExamNotClosed && !isAdmin;

                let notaElement;
                if (hideNota) {
                    notaElement = (
                        <Tooltip title="La nota no es mostrarà fins que l'examen estigui Tancat">
                            <Tag color="default" style={{ cursor: 'help' }}>🔒 Pendent</Tag>
                        </Tooltip>
                    );
                } else if (record.hasTests) {
                    const rounded = Number(record.nota).toFixed(2);
                    notaElement = <Tag color={record.nota >= 50 ? "green" : "red"}>{rounded}</Tag>;
                } else {
                    notaElement = <Tag color="purple">N/P</Tag>;
                }

                const isActive = record.inici && dayjs(record.inici.toString().substring(0, 19)).isBefore(dayjs());

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {notaElement}
                            {(record.hasTests === true || record.hasTests === 1 || record.hasTests === "1" || record.hasTests === "true") && 
                             record.origen_success !== undefined && record.origen_success !== null && (
                                <ModoDesafioTag origenSuccess={record.origen_success} />
                            )}
                        </div>
                        {(isAdmin || modoDesafio) && (
                            <RecompensesTagsPractica
                                id_practica={record.id + ""}
                                isUnlocked={isActive || record.estat === 3 || record.estat === 2 || isAdmin}
                                ele={record}
                                refreshKey={recompensesRefreshKeys[record.id] || 0}
                                modoDesafio={modoDesafio}
                            />
                        )}
                    </div>
                );
            }
        }
    ];

    const teacherColumns: ColumnsType<any> = [
        {
            key: 'status_summary',
            title: 'Entregues',
            align: 'center',
            render: (text, record) => (
                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                    <Tag color="green" style={{ fontWeight: 'bold' }}>🟢 {record.ok?.length || 0}</Tag>
                    <Tag color="red" style={{ fontWeight: 'bold' }}>🔴 {record.ko?.length || 0}</Tag>
                    <Tag color="purple" style={{ fontWeight: 'bold' }}>🟣 {record.np?.length || 0}</Tag>
                </div>
            )
        }
    ];

    const actionColumns: ColumnsType<any> = [
        {
            key: 'actions',
            title: 'Accions',
            align: 'center',
            render: (e, ele) => (
                <Space>
                    {(() => {
                        const canShowBase = (user.tipus === "0" || user.tipus === "1" || (user.tipus === "2" && ele.estat !== 1 && ele.is_examen !== 1));
                        if (!canShowBase) return null;

                        // Si el usuario es alumno y el aula está en modo examen, se bloquea el acceso/revisión de prácticas regulares
                        if (user.tipus === "2") {
                            const userAulas = alumne?.aules || [];
                            const currentAula = (userAulas || []).find((a: any) => String(a.id || a.id_aula) === String(ele.id_aula));
                            const isAulaExamMode = Boolean(
                                ele.aula_is_examen === 1 || ele.aula_is_examen === true ||
                                (currentAula && (currentAula.isExamen === 1 || currentAula.isExamen === true || currentAula.is_examen === 1 || currentAula.is_examen === true))
                            );
                            if (isAulaExamMode) return null;
                        }

                        // Si ya se ha intentado en modo normal (primer intento normal = 0, o tiene entregas con origen_success === 0),
                        // no se ha de poder intentar en desafío.
                        const hasNormalAttempt = ele.first_origen === 0 || (ele.first_origen === null && ele.hasTests && ele.origen_success === 0);
                        const isChallengeAttempt = modoDesafio && ele.estat === 3;

                        // Si estamos en modo desafío y ya se intentó en normal, el botón de desafío no ha de aparecer.
                        if (isChallengeAttempt && hasNormalAttempt) {
                            return null;
                        }

                        // Bloqueo de acceso por prerrequisito: para todos los alumnos si la práctica tiene configurada una práctica previa requerida
                        const isStudent = user.tipus !== "0" && user.tipus !== "1";
                        const isLocked = isStudent && Boolean(ele.id_bloqueo) && !Boolean(ele.bloqueo_intentado && ele.bloqueo_intentado !== 0 && ele.bloqueo_intentado !== "0");

                        if (isLocked) {
                            const reqName = ele.bloqueo_nom || "la práctica previa requerida";
                            return (
                                <Tooltip title={`🔒 Bloqueado: debes realizar al menos un intento en "${reqName}" para desbloquear el acceso.`}>
                                    <Button
                                        disabled
                                        shape="circle"
                                        icon={<LockOutlined />}
                                        style={{ backgroundColor: '#f5f5f5', border: '1px solid #d9d9d9', color: '#bfbfbf', cursor: 'not-allowed' }}
                                    />
                                </Tooltip>
                            );
                        }

                        const isRevisar = ele.estat !== 3;
                        const buttonTitle = isRevisar ? "Revisar" : (isChallengeAttempt ? "Iniciar Desafiament" : "Accedir");
                        const buttonColor = isRevisar ? "orangered" : (isChallengeAttempt ? "darkred" : JutgeColors.colors.color2);
                        const buttonIcon = isChallengeAttempt ? <span style={{ fontSize: "16px" }}>⚔️</span> : <BiArrowFromLeft />;

                        return (
                            <Tooltip title={buttonTitle}>
                                <Button
                                    style={{ backgroundColor: buttonColor, border: 'none', color: '#fff' }}
                                    shape="circle"
                                    icon={buttonIcon}
                                    onClick={() => {
                                        if (ele.inici) {
                                            const date = dayjs(ele.inici.toString().substring(0, 19));
                                            if (date.isAfter(dayjs()) && user.tipus !== "1" && user.tipus !== "0") {
                                                message.warning("La práctica aún no ha empezado.");
                                                return;
                                            }
                                        }
                                        if (isChallengeAttempt) {
                                            startExamModal(ele, true);
                                        } else {
                                            navigate(`/jutge/practiques/${ele.id}`);
                                        }
                                    }}
                                />
                            </Tooltip>
                        );
                    })()}


                    {/* Reward button for students - solo si se completó en Modo Desafío (1) */}
                    {modoDesafio && user.tipus === "2" && ele.has_rewards === 1 && ele.reward_obtained === 0 && ele.nota >= 50 && ele.origen_success === 1 && (
                        <Tooltip title="Reclamar Recompensa">
                            <Button
                                shape="circle"
                                icon={<GiftOutlined />}
                                onClick={() => handleClaimRewardClick(ele)}
                                style={{ backgroundColor: 'gold', border: 'none', color: '#000' }}
                            />
                        </Tooltip>
                    )}

                    {/* Reward button for students - completado en Modo Juego (2) */}
                    {modoDesafio && user.tipus === "2" && ele.has_rewards === 1 && ele.reward_obtained === 0 && ele.nota >= 50 && ele.origen_success === 2 && (
                        <Tooltip title="Recompensa disponible en el juego (Unity)">
                            <Button
                                disabled
                                shape="circle"
                                icon={<GiftOutlined />}
                                style={{ backgroundColor: '#f0f0f0', border: 'none', color: '#aaa', cursor: 'not-allowed' }}
                            />
                        </Tooltip>
                    )}

                    {/* Exam Access for Student: only shown when backend estat===3 (Visible+within dates) */}
                    {(user.tipus === "2" && ele.is_examen === 1 && !ele.started_exam && ele.estat === 3) && (
                        <Tooltip title="Començar Examen (Un sol ús)">
                            <Button
                                type="primary"
                                style={{ backgroundColor: JutgeColors.colors.color4, border: 'none', color: '#fff' }}
                                icon={<FaLaptopCode />}
                                onClick={() => startExamModal(ele)}
                            >
                                Iniciar exàmen
                            </Button>
                        </Tooltip>
                    )}

                    {/* IDE Access for Student (is_ide) - només si no és examen (els exàmens tenen el botó únic 'Iniciar exàmen') */}
                    {(user.tipus === "2" && ele.is_ide === 1 && ele.is_examen !== 1) && (
                        (() => {
                            if (ele.inici && dayjs(ele.inici.toString().substring(0, 19)).isAfter(dayjs())) return null;
                            if (ele.estat !== 3) return null;
                            const hasNormalAttempt = ele.first_origen === 0 || (ele.first_origen === null && ele.hasTests && ele.origen_success === 0);
                            const ideMenuItems = [
                                {
                                    key: 'ide-direct',
                                    label: 'Obrir IDE Normal',
                                    icon: <BiCode />,
                                    onClick: () => startExamModal(ele, false)
                                },
                                ...(!hasNormalAttempt ? [{
                                    key: 'ide-wrapper',
                                    label: 'Obrir Mode Simulació d\'Examen',
                                    icon: <DesktopOutlined />,
                                    onClick: () => startExamModal(ele, true)
                                }] : [])
                            ];
                            return (
                                <Dropdown
                                    menu={{ items: ideMenuItems }}
                                    trigger={['click']}
                                >
                                    <Tooltip title="Opcions IDE">
                                        <Button
                                            type="primary"
                                            style={{ backgroundColor: '#0ea5e9', border: 'none', color: '#fff' }}
                                            icon={<BiCode />}
                                        >
                                            Obrir IDE
                                        </Button>
                                    </Tooltip>
                                </Dropdown>
                            );
                        })()
                    )}

                    {/* IDE Buttons for Admin/Teacher (is_ide) */}
                    {(user.tipus === "0" || user.tipus === "1") && ele.is_ide === 1 && ele.is_examen !== 1 && (
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'simulate-ide-direct',
                                        label: 'Obrir IDE directament',
                                        icon: <BiCode />,
                                        onClick: () => startExamModal(ele, false)
                                    },
                                    {
                                        key: 'simulate-ide-wrapper',
                                        label: 'Simular Examen',
                                        icon: <DesktopOutlined />,
                                        onClick: () => startExamModal(ele, true)
                                    },
                                    {
                                        key: 'eval-me-ide',
                                        label: 'Avaluar el meu codi',
                                        icon: <BiRefresh style={{ color: '#5b21b6' }} />,
                                        onClick: () => submitFromIde(ele, 'single')
                                    },
                                    {
                                        key: 'eval-bulk-ide',
                                        label: 'Avaluar Massiu',
                                        icon: <PlayCircleOutlined style={{ color: '#059669' }} />,
                                        onClick: () => submitFromIde(ele, 'bulk')
                                    }
                                ]
                            }}
                            trigger={['click']}
                        >
                            <Tooltip title="Opcions IDE">
                                <Button
                                    shape="circle"
                                    style={{ backgroundColor: '#0ea5e9', border: 'none', color: '#fff' }}
                                    icon={<BiCode />}
                                />
                            </Tooltip>
                        </Dropdown>
                    )}

                    {/* Exam Students for Admin */}
                    {(user.tipus === "0" || user.tipus === "1") && ele.is_examen === 1 && (
                        <>
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: 'simulate',
                                            label: 'Simular Examen',
                                            icon: <DesktopOutlined />,
                                            onClick: () => startExamModal(ele)
                                        },
                                        {
                                            key: 'eval-me',
                                            label: 'Evaluar mi código',
                                            icon: <BiRefresh style={{ color: '#5b21b6' }} />,
                                            onClick: () => submitFromIde(ele, 'single')
                                        },
                                        {
                                            key: 'eval-bulk',
                                            label: 'Evaluar Masivo',
                                            icon: <PlayCircleOutlined style={{ color: '#059669' }} />,
                                            onClick: () => submitFromIde(ele, 'bulk')
                                        }
                                    ]
                                }}
                                trigger={['click']}
                            >
                                <Tooltip title="Opciones de Examen/IDE">
                                    <Button
                                        shape="circle"
                                        style={{ backgroundColor: '#1890ff', border: 'none', color: '#fff' }}
                                        icon={<DesktopOutlined />}
                                    />
                                </Tooltip>
                            </Dropdown>
                        </>
                    )}

                    {(() => {
                        if (user.tipus !== "2" || ele.estat !== 2) return null;
                        
                        // Si el aula está en modo examen, no se muestra el botón de Revisar
                        const userAulas = alumne?.aules || [];
                        const currentAula = (userAulas || []).find((a: any) => String(a.id || a.id_aula) === String(ele.id_aula));
                        const isAulaExamMode = Boolean(
                            ele.aula_is_examen === 1 || ele.aula_is_examen === true ||
                            (currentAula && (currentAula.isExamen === 1 || currentAula.isExamen === true || currentAula.is_examen === 1 || currentAula.is_examen === true))
                        );

                        if (isAulaExamMode) return null;

                        return (
                            <Tooltip title="Revisar">
                                <Button shape="circle" icon={<FaEye />}
                                    style={{ backgroundColor: JutgeColors.colors.color3, border: 'none', color: '#fff' }}
                                    onClick={() => {
                                        setSelectedPracticaView(ele);
                                        setModalViewVisible(true);
                                    }} />
                            </Tooltip>
                        );
                    })()}
                    {/* Admin Actions */}
                    {(user.tipus === "0" || user.tipus === "1") && (
                        <>
                            <Tooltip title="Panell d'Alumnes i Avaluació (Complet)">
                                <Button
                                    shape="circle"
                                    icon={<FaUsers />}
                                    style={{ backgroundColor: '#722ed1', border: 'none', color: '#fff' }}
                                    onClick={() => {
                                        setSelectedPracticaForAlumnes(ele);
                                        setPracticaAlumnesModalVisible(true);
                                    }}
                                />
                            </Tooltip>
                            <Tooltip title={ele.id_bloqueo ? `Bloqueo activo: requiere "${ele.bloqueo_nom || ('Práctica #' + ele.id_bloqueo)}"` : "Configurar bloqueo de acceso"}>
                                <Button
                                    shape="circle"
                                    icon={<LockOutlined />}
                                    style={{
                                        backgroundColor: ele.id_bloqueo ? '#fff7e6' : '#fafafa',
                                        borderColor: ele.id_bloqueo ? '#ffa940' : '#d9d9d9',
                                        color: ele.id_bloqueo ? '#fa8c16' : '#8c8c8c'
                                    }}
                                    onClick={() => handleOpenBloqueoModal(ele)}
                                />
                            </Tooltip>
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: 'notes-resum',
                                            label: 'Resum Notes',
                                            icon: <FaUsers style={{ color: JutgeColors.colors.color3 }} />,
                                            onClick: () => {
                                                setSelectedPractica(ele);
                                                setAlumnesData([
                                                    ...(ele.ok || []),
                                                    ...(ele.ko || []),
                                                    ...(ele.np || []),
                                                ]);
                                                setAlumnesModalVisible(true);
                                            }
                                        },
                                        {
                                            key: 'configurar',
                                            label: 'Configurar',
                                            icon: <VscSettingsGear style={{ color: JutgeColors.colors.color4 }} />,
                                            onClick: () => showSettings(ele)
                                        },
                                        {
                                            key: 'bloqueo',
                                            label: 'Bloqueo de acceso',
                                            icon: <LockOutlined style={{ color: ele.id_bloqueo ? '#fa8c16' : '#8c8c8c' }} />,
                                            onClick: () => handleOpenBloqueoModal(ele)
                                        },
                                        {
                                            key: 'duplicar',
                                            label: 'Duplicar',
                                            icon: <MdContentCopy style={{ color: JutgeColors.colors.color5 }} />,
                                            onClick: () => {
                                                setSelectedPractica(ele);
                                                setDuplicarModal(true);
                                            }
                                        },
                                        {
                                            key: 'documents',
                                            label: 'Documents',
                                            icon: <IoDocumentTextOutline style={{ color: JutgeColors.colors.color4 }} />,
                                            onClick: () => {
                                                setSelectedPractica(ele);
                                                setDocumentModalVisible(true);
                                            }
                                        },
                                        {
                                            key: 'premis',
                                            label: 'Gestionar Recompenses',
                                            icon: <TrophyOutlined style={{ color: 'gold' }} />,
                                            onClick: () => {
                                                setSelectedRecompensaPractica(ele);
                                                setRankingsVisible(true);
                                            }
                                        },
                                        {
                                            key: 'pujar',
                                            label: 'Pujar entregues',
                                            icon: <MdOutlineDriveFolderUpload style={{ color: JutgeColors.colors.color1 }} />,
                                            onClick: () => {
                                                setSelectedPractica(ele);
                                                setUploadFolderModalVisible(true);
                                            }
                                        },
                                        {
                                            key: 'ide-eval',
                                            label: 'Avaluar Massiu (IDE)',
                                            icon: <BsLaptop style={{ color: '#1890ff' }} />,
                                            onClick: () => submitFromIde(ele, 'bulk')
                                        }
                                    ]
                                }}
                                trigger={['click']}
                            >
                                <Button shape="circle" icon={<MoreOutlined />} />
                            </Dropdown>
                        </>
                    )}
                </Space>
            )
        }
    ];

    const columns: ColumnsType<any> =
        (user.tipus === "0" || user.tipus === "1")
            ? [...baseColumns, ...teacherColumns, ...actionColumns]
            : [...baseColumns, ...actionColumns];

    const rowSelection = isAdmin ? {
        selectedRowKeys,
        onChange: (keys: React.Key[]) => setSelectedRowKeys(keys)
    } : undefined;

    const tableContent = (
        <>
            {selectedRowKeys.length > 0 && (
                <div style={{
                    marginBottom: 12,
                    padding: '10px 16px',
                    background: '#e6f7ff',
                    border: '1px solid #91d5ff',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 6px rgba(24,144,255,0.1)'
                }}>
                    <Space size="middle">
                        <span style={{ color: '#0050b3', fontWeight: 600 }}>
                            {selectedRowKeys.length} {onlyExams ? 'exàmens seleccionats' : 'pràctiques seleccionades'}
                        </span>
                        <Button
                            type="primary"
                            icon={<ApartmentOutlined />}
                            onClick={() => setModalAssignarRAs(true)}
                            style={{ borderRadius: 6 }}
                        >
                            Assignar / Modificar RAs
                        </Button>
                    </Space>
                    <Button size="small" type="link" onClick={() => setSelectedRowKeys([])}>
                        Deseleccionar tot
                    </Button>
                </div>
            )}
            <Table
                loading={loading}
                pagination={isAdmin ? false : { pageSize: 8 }}
                columns={columns}
                dataSource={filteredDades}
                rowKey="id"
                rowSelection={rowSelection}
                size="small"
                bordered={false}
                // Renderitza els desplegables (filtres) al body per evitar que la Card
                // amb overflow els retalli i els mostri per sota de la taula.
                getPopupContainer={() => document.body}
                components={isAdmin ? { body: { row: TableDragRow } } : undefined}
                style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: '#fff'
                }}
            />
        </>
    );

    return (
        <>
            <AppSider currentVal={onlyExams ? 9 : 3} setPantalla={setPantalla} />
            <Content style={{ background: '#f8f9fb', padding: '24px', minHeight: '100vh' }}>
                {/* Capçalera amb títol, cercador i refrescar */}
                <div style={{
                    marginBottom: 20,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 16,
                    flexWrap: 'wrap'
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Typography.Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                {onlyExams ? (
                                    <><FireOutlined style={{ color: '#fa8c16' }} /> Llistat d'Exàmens</>
                                ) : (
                                    <><CodeOutlined style={{ color: '#1890ff' }} /> Llistat de Pràctiques</>
                                )}
                            </Typography.Title>
                            {Array.isArray(dades) && (
                                <Badge
                                    count={filteredDades.length}
                                    style={{ backgroundColor: onlyExams ? '#fa8c16' : '#1890ff', fontWeight: 600 }}
                                    overflowCount={999}
                                />
                            )}
                        </div>
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                            {onlyExams
                                ? "Consulta, edita i supervisa les proves d'avaluació en mode examen"
                                : "Consulta, edita i gestiona les pràctiques d'aprenentatge i avaluació"}
                        </Typography.Text>
                    </div>

                    <Space wrap size="middle">
                        {/* Filtre per Aula */}
                        {availableAulas.length > 0 && (
                            <Select
                                value={selectedAula}
                                onChange={(val) => setSelectedAula(val)}
                                disabled={availableAulas.length === 1}
                                style={{ minWidth: 160, borderRadius: 8 }}
                                options={[
                                    ...(availableAulas.length > 1 ? [{ label: "🏫 Totes les aules", value: 'all' }] : []),
                                    ...availableAulas.map(a => ({
                                        label: `🏫 ${a.nom}`,
                                        value: a.id
                                    }))
                                ]}
                            />
                        )}

                        <Input
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Cercar per nom, aula o ID..."
                            allowClear
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 240, borderRadius: 8 }}
                        />

                        {/* Modo Desafiament toggle (visible for all roles except in examens tab) */}
                        {!onlyExams && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                background: modoDesafio ? 'linear-gradient(90deg, #fff1f0, #ffe8e6)' : '#f5f5f5',
                                padding: '4px 12px',
                                borderRadius: 20,
                                border: modoDesafio ? '1px solid #ffa39e' : '1px solid #e8e8e8'
                            }}>
                                <span style={{
                                    fontWeight: 600,
                                    fontSize: 13,
                                    color: modoDesafio ? '#cf1322' : '#595959',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4
                                }}>
                                    {modoDesafio ? '⚔️' : '🛡️'} Mode Desafiament
                                </span>
                                <Switch size="small" checked={modoDesafio} onChange={handleToggleModoDesafio} />
                            </div>
                        )}

                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={() => checkDades()}
                            loading={loading}
                            style={{ borderRadius: 8 }}
                        >
                            Refrescar
                        </Button>
                    </Space>
                </div>

                {/* Targetes de resum KPI (Estil unificat amb ActivitatsIdExercicis) */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={isAdmin ? 6 : 6}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Pràctiques Totals</span>}
                                value={stats.total}
                                prefix={<CodeOutlined style={{ color: '#1890ff', marginRight: 6 }} />}
                                valueStyle={{ fontWeight: 700, fontSize: 26 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Pràctiques Actives</span>}
                                value={stats.active}
                                suffix={<span style={{ fontSize: 14, color: '#8c8c8c' }}>/ {stats.total}</span>}
                                prefix={<CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />}
                                valueStyle={{ color: '#52c41a', fontWeight: 700, fontSize: 26 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Mode Examen</span>}
                                value={stats.exams}
                                prefix={<FireOutlined style={{ color: '#fa8c16', marginRight: 6 }} />}
                                valueStyle={{ color: '#fa8c16', fontWeight: 700, fontSize: 26 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>{isAdmin ? "Entregades" : "Nota Mitjana"}</span>}
                                value={isAdmin ? stats.completed : stats.avgNota}
                                suffix={isAdmin ? undefined : "%"}
                                prefix={<AreaChartOutlined style={{ color: '#722ed1', marginRight: 6 }} />}
                                valueStyle={{ color: '#722ed1', fontWeight: 700, fontSize: 26 }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Taula amb Card moderna */}
                <Card bordered={false} style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.08)', background: '#fff', borderRadius: '12px' }}>
                    {isAdmin && !searchText.trim() ? (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                            <SortableContext items={filteredDades.map(i => i.id)} strategy={verticalListSortingStrategy}>
                                {tableContent}
                            </SortableContext>
                        </DndContext>
                    ) : (
                        tableContent
                    )}
                </Card>
            </Content>

            {!loading && (
                <FloatButton.Group
                    trigger="click"
                    type="primary"
                    style={{ insetInlineStart: 97 }}
                    icon={<IoOptions />}
                >
                    {(user.tipus === "0" || user.tipus === "1") && (
                        <FloatButton
                            shape="circle"
                            onClick={() => {
                                setSelected({ id: 0, estat: 1, tipus: 1, is_examen: onlyExams ? 1 : 0 });
                                setModal(true);
                            }}
                            style={{ backgroundColor: AvaluacioColors.colors.color6 }}
                            icon={<BiPlus />}
                        />
                    )}
                    <FloatButton
                        shape="circle"
                        type="default"
                        onClick={checkDades}
                        style={{ backgroundColor: JutgeColors.colors.color6 }}
                        icon={<BiRefresh />}
                    />
                </FloatButton.Group>
            )}

            {modal && (
                <ConfiguracioModal
                    activitat={selected}
                    visible={modal}
                    onClose={onCloseModal}
                    defaultTipus={1}
                    fixedIsExamen={selected?.id === 0 ? (onlyExams ? 1 : 0) : undefined}
                    availablePracticas={dades}
                />
            )}
            {modalE && <ExercicisModal nom={selected.nom} id_activitat={selected.id} visible={modalE} onClose={onCloseModal} />}
            {duplicarModal && selectedPractica && (
                <CopyModal id_practica={selectedPractica.id} visible={duplicarModal} onClose={(updated) => {
                    setDuplicarModal(false);
                    setSelectedPractica(null);
                    if (updated) checkDades();
                }} />
            )}

            <Modal open={modalVisible} closable={false} maskClosable={false} footer={null} centered>
                <h3>¡Atención! Has cambiado de pestaña o minimizado la ventana</h3>
                <Input.Password
                    placeholder="Introduce la password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />
                <Button type="primary" style={{ marginTop: "10px" }} onClick={handlePasswordSubmit}>
                    Enviar
                </Button>
            </Modal>

            {modalBloqueoVisible && selectedPracticaBloqueo && (
                <Modal
                    open={modalBloqueoVisible}
                    title={
                        <Space>
                            <LockOutlined style={{ color: '#fa8c16' }} />
                            <span>Bloqueo de acceso (Prerrequisito) - {selectedPracticaBloqueo.nom}</span>
                        </Space>
                    }
                    onCancel={() => {
                        setModalBloqueoVisible(false);
                        setSelectedPracticaBloqueo(null);
                    }}
                    onOk={handleSaveBloqueo}
                    confirmLoading={savingBloqueo}
                    okText="Guardar"
                    cancelText="Cancelar"
                    centered
                >
                    <div style={{ padding: '8px 0' }}>
                        <p style={{ color: '#595959', fontSize: 13, marginBottom: 16 }}>
                            Configura qué práctica previa debe haber sido intentada <strong>al menos una vez</strong> por el alumno antes de poder acceder a esta práctica.
                            <br />
                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                (Este bloqueo solo se aplica en <strong>Modo Desafío</strong> y <strong>Modo Juego</strong>; en el modo normal el acceso no se bloquea).
                            </span>
                        </p>

                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Práctica previa requerida:</div>
                        <Select
                            allowClear
                            placeholder="Sin bloqueo (Acceso libre)"
                            style={{ width: '100%' }}
                            value={selectedBloqueoId}
                            onChange={(val) => setSelectedBloqueoId(val)}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option: any) => {
                                const text = Array.isArray(option?.children) 
                                    ? option.children.join(' ') 
                                    : String(option?.children ?? option?.label ?? '');
                                return text.toLowerCase().includes(input.toLowerCase());
                            }}
                        >
                            {(dades || [])
                                .filter((p: any) => {
                                    if (p.id === selectedPracticaBloqueo.id) return false;
                                    // Solo de la misma aula
                                    const pAula = p.id_aula ?? null;
                                    const targetAula = selectedPracticaBloqueo.id_aula ?? null;
                                    if (pAula !== targetAula) return false;
                                    // Solo prácticas (tipus === 1)
                                    const pTipus = p.tipus_aula ?? p.tipus ?? 0;
                                    return Number(pTipus) === 1;
                                })
                                .map((p: any) => (
                                    <Select.Option key={p.id} value={p.id}>
                                        🔒 {p.nom} {p.aula ? `(${p.aula})` : ''}
                                    </Select.Option>
                                ))
                            }
                        </Select>
                    </div>
                </Modal>
            )}

            {modalViewVisible && selectedPracticaView && (
                <Modal
                    open={modalViewVisible}
                    onCancel={() => { setModalViewVisible(false); setSelectedPracticaView(null); }}
                    width="90%"
                    footer={null}
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span>Pràctica: {selectedPracticaView.nom}</span>
                            {(selectedPracticaView.hasTests === true || selectedPracticaView.hasTests === 1 || selectedPracticaView.hasTests === "1" || selectedPracticaView.hasTests === "true") && 
                             selectedPracticaView.origen_success !== undefined && selectedPracticaView.origen_success !== null && (
                                <ModoDesafioTag origenSuccess={selectedPracticaView.origen_success} />
                            )}
                        </div>
                    }
                    centered
                    style={{ top: 20 }}
                    bodyStyle={{ padding: 0, background: '#fafafa' }}
                >
                    <PracticaIdTests id_practica={selectedPracticaView.id} />
                </Modal>
            )}

            {practicaAlumnesModalVisible && selectedPracticaForAlumnes && (
                <Modal
                    open={practicaAlumnesModalVisible}
                    onCancel={() => {
                        setPracticaAlumnesModalVisible(false);
                        setSelectedPracticaForAlumnes(null);
                    }}
                    width="95%"
                    footer={null}
                    title={`Avaluació i Alumnes: ${selectedPracticaForAlumnes.nom} (ID #${selectedPracticaForAlumnes.id})`}
                    centered
                    style={{ top: 20 }}
                    bodyStyle={{ padding: '16px 24px', background: '#fafafa', maxHeight: '85vh', overflowY: 'auto' }}
                    destroyOnClose
                >
                    <PracticaIdAlumnes id_practica={selectedPracticaForAlumnes.id} />
                </Modal>
            )}

            <Modal
                open={ideStartModalVisible}
                maskClosable={false}
                closable={false}
                footer={null}
                centered
                title={ideStartExamEl?.is_examen === 1 ? "💻 Mi Entorno de Desarrollo (Examen)" : (modoDesafio ? "⚔️ Iniciar Desafío" : "💻 Mi Entorno de Desarrollo")}
            >
                <div style={{ textAlign: 'center', minHeight: 180, padding: '20px 0' }}>
                    {ideStartLoading ? (
                        <>
                            <div style={{ border: '5px solid #f3f3f3', borderTop: '5px solid #3498db', borderRadius: '50%', width: 40, height: 40, animation: 'spin 1s linear infinite', margin: '20px auto' }}></div>
                            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                            <h3 style={{ marginTop: 20 }}>Preparando entorno...</h3>
                            <p>Esperando a que el contenedor Docker arranque. Esto puede tardar 1-2 minutos.</p>
                            <Button
                                danger
                                style={{ marginTop: 20 }}
                                onClick={() => {
                                    Modal.confirm({
                                        title: '¿Confirmar Salida?',
                                        content: 'No podrás volver a entrar al examen si sales de esta pantalla. ¿Estás seguro?',
                                        okText: 'Sí, Salir',
                                        cancelText: 'Cancelar',
                                        okButtonProps: { danger: true },
                                        onOk: () => setIdeStartModalVisible(false)
                                    });
                                }}
                            >
                                Cancelar
                            </Button>
                        </>
                    ) : ideStartError ? (
                        <>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
                            <h3 style={{ color: '#cf1322' }}>Error al preparar el entorno</h3>
                            <p style={{ color: '#666', marginBottom: 20 }}>{ideStartError}</p>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={() => ideStartExamEl && startExamProcess(ideStartExamEl, !modoDesafio ? false : true)}
                                    style={{ background: '#722ed1', borderColor: '#722ed1' }}
                                >
                                    🔄 Reintentar
                                </Button>
                                <Button
                                    size="large"
                                    danger
                                    onClick={() => setIdeStartModalVisible(false)}
                                >
                                    Cancelar
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <DesktopOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
                            <h3>{ideStartExamEl?.is_examen === 1 ? '🚀 Entorno de Examen Listo' : '🚀 Entorno Listo'}</h3>
                            <p>{ideStartExamEl?.is_examen === 1
                                ? 'Importante: No cierres y NO CAMBIES DE PESTAÑA. Tu examen se bloqueará por seguridad si detectamos que estás usando otra pestaña.'
                                : 'Tu entorno de desarrollo está preparado. Puedes acceder ahora.'}
                            </p>
                            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', gap: 10 }}>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<ExportOutlined />}
                                    onClick={() => {
                                        setIdeStartModalVisible(false);
                                        window.open(ideStartUrl, '_blank');
                                    }}
                                    style={{ background: '#722ed1', borderColor: '#722ed1' }}
                                >
                                    {isAdmin ? (ideStartExamEl?.is_examen === 1 ? 'Iniciar Simulación' : 'Entrar a la IDE') : (ideStartExamEl?.is_examen === 1 ? 'Iniciar Examen Completamente' : (modoDesafio ? 'Iniciar Desafío' : 'Entrar a la IDE'))}
                                </Button>

                                <Button
                                    size="large"
                                    danger
                                    onClick={() => {
                                        Modal.confirm({
                                            title: '¿Confirmar Salida?',
                                            content: 'No podrás volver a entrar al examen si sales de esta pantalla. ¿Estás seguro?',
                                            okText: 'Sí, Salir',
                                            cancelText: 'Cancelar',
                                            okButtonProps: { danger: true },
                                            onOk: () => setIdeStartModalVisible(false)
                                        });
                                    }}
                                >
                                    Salir
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>

            <Modal
                open={alumnesModalVisible}
                onCancel={() => setAlumnesModalVisible(false)}
                width="50%"
                footer={null}
                title={`Alumnes de la pràctica: ${selectedPractica?.nom ?? ''}`}
                centered
            >
                <Table
                    dataSource={alumnesData}
                    rowKey={(record) => record.id}
                    pagination={{ pageSize: 10 }}
                    size={"small"}
                    bordered
                    columns={[
                        {
                            title: 'Nom',
                            dataIndex: 'nom',
                            key: 'nom',
                            render: (text, record) => <Text strong>{text} {record.cognoms ?? ''}</Text>,
                        },
                        {
                            title: 'Grup',
                            dataIndex: 'grup',
                            key: 'grup',
                            filters: [...Array.from(new Set(alumnesData.map(a => a.grup))).map(g => ({ text: g, value: g }))],
                            onFilter: (value, record) => record.grup === value,
                            render: (grup) => <Tag color="blue" style={{ fontWeight: 'bold' }}>{grup}</Tag>,
                        },
                        {
                            title: 'Nota',
                            dataIndex: 'nota',
                            key: 'nota',
                            align: 'center',
                            render: (nota) => {
                                if (nota == null) return <Tag color="purple" style={{ fontWeight: 'bold' }}>N/P</Tag>;
                                return <Tag color={nota >= 50 ? 'green' : 'red'} style={{ fontWeight: 'bold' }}>{nota.toFixed(2)}%</Tag>;
                            },
                        },
                    ]}
                />
            </Modal>

            {documentModalVisible && selectedPractica && (
                <DocumentModal
                    id_practica={selectedPractica.id}
                    practicaNom={selectedPractica.nom}
                    visible={documentModalVisible}
                    onClose={(updated: boolean) => {
                        setDocumentModalVisible(false);
                        if (updated) checkDades();
                    }}
                />
            )}

            {uploadFolderModalVisible && selectedPractica && (
                <UploadFolderModal
                    id_practica={selectedPractica.id}
                    visible={uploadFolderModalVisible}
                    onClose={(updated: any) => {
                        setUploadFolderModalVisible(false);
                        if (updated) checkDades();
                    }}
                />
            )}

            {/* IDE Eval Result Modal */}
            <Modal
                open={ideEvalModalVisible}
                onCancel={() => { if (!ideEvalLoading) { setIdeEvalModalVisible(false); setIdeEvalResult(null); } }}
                closable={!ideEvalLoading}
                maskClosable={!ideEvalLoading}
                width={isBulkEval ? "80%" : "70%"}
                footer={null}
                title={`🖥️ Evaluació IDE: ${ideEvalPractica?.nom ?? ''}`}
                centered
            >
                {ideEvalLoading && !ideEvalResult?.results ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                        {isBulkEval ? (
                            <div style={{ marginTop: 20 }}>
                                <Typography.Title level={4}>Evaluació Massiva en curs...</Typography.Title>
                                <Typography.Text type="secondary">Processant: {ideEvalProgress.currentName}</Typography.Text>
                                <div style={{ marginTop: 10, width: '100%', maxWidth: 400, margin: '10px auto', height: 10, background: '#eee', borderRadius: 5, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', background: '#1890ff', width: `${(ideEvalProgress.current / ideEvalProgress.total) * 100}%`, transition: 'width 0.3s' }} />
                                </div>
                                <p>{ideEvalProgress.current} / {ideEvalProgress.total}</p>
                            </div>
                        ) : (
                            <p style={{ marginTop: 16 }}>Llegint fitxers del IDE i executant tests...</p>
                        )}
                    </div>
                ) : ideEvalResult?.error ? (
                    <Result
                        status="error"
                        title="Error en l'avaluació"
                        subTitle={ideEvalResult.error}
                    />
                ) : ideEvalResult?.bulk ? (
                    <div style={{ padding: 20 }}>
                        <Typography.Title level={4}>Resultats de l'evaluació massiva {ideEvalLoading && <Spin size="small" style={{ marginLeft: 10 }} />}</Typography.Title>

                        {ideEvalLoading && (
                            <div style={{ marginBottom: 20 }}>
                                <Typography.Text type="secondary">Processant: {ideEvalProgress.currentName}</Typography.Text>
                                <div style={{ marginTop: 10, width: '100%', height: 10, background: '#eee', borderRadius: 5, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', background: '#1890ff', width: `${(ideEvalProgress.current / ideEvalProgress.total) * 100}%`, transition: 'width 0.3s' }} />
                                </div>
                                <p>{ideEvalProgress.current} / {ideEvalProgress.total}</p>
                            </div>
                        )}

                        <Table
                            size="small"
                            dataSource={ideEvalResult.results}
                            rowKey="name"
                            pagination={false}
                            scroll={{ y: 400 }}
                            columns={[
                                { title: 'Alumne', dataIndex: 'name', key: 'name' },
                                {
                                    title: 'Estat',
                                    dataIndex: 'status',
                                    key: 'status',
                                    render: (s) => <Tag color={s === 'ok' ? 'green' : s === 'skipped' ? 'orange' : 'red'}>{s.toUpperCase()}</Tag>
                                },
                                {
                                    title: 'Resultat',
                                    key: 'res',
                                    render: (_, r) => r.status === 'ok' ? (
                                        <Tag color={r.response === 'OK' ? 'success' : 'error'}>{r.response}</Tag>
                                    ) : <Text type="secondary" style={{ fontSize: 11 }}>{r.error}</Text>
                                }
                            ]}
                        />
                        <Button style={{ marginTop: 20 }} block type="primary" onClick={() => setIdeEvalModalVisible(false)} disabled={ideEvalLoading}>Tancar</Button>
                    </div>
                ) : ideEvalResult ? (
                    <div>
                        {(ideEvalResult.response === 'OK' || ideEvalResult.response === 'Incorrect') ? (
                            <>
                                <div style={{ marginBottom: 12 }}>
                                    <Tag color="blue" style={{ fontSize: '0.9rem' }}>Tests trobats: —</Tag>
                                    <Tag color={ideEvalResult.response === 'OK' ? 'green' : 'red'} style={{ fontSize: '0.9rem' }}>
                                        {ideEvalResult.response === 'OK' ? '✅ Tots els tests superats' : '❌ Alguns tests han fallat'}
                                    </Tag>
                                </div>
                                <Tag color={ideEvalResult.response === 'OK' ? 'green' : 'orange'}>
                                    {ideEvalResult.response}
                                </Tag>
                            </>
                        ) : (
                            <Result
                                status="warning"
                                title="Resposta"
                                subTitle={JSON.stringify(ideEvalResult)}
                            />
                        )}
                        <Button style={{ marginTop: 20 }} block type="primary" onClick={() => setIdeEvalModalVisible(false)}>Tancar</Button>
                    </div>
                ) : null}
            </Modal>

            {recompensaModalVisible && (
                <RecompensaSummaryModal
                    visible={recompensaModalVisible}
                    onClose={() => {
                        setRecompensaModalVisible(false);
                        if (pendingJavamonBattle) {
                            setActiveRewardData({ idActivitat: "0", idProblema: pendingJavamonBattle.idProblema, userId: user.id + "" });
                            setBattleJavamon(pendingJavamonBattle.javamon);
                            setShowBattle(true);
                            setPendingJavamonBattle(null);
                        }
                    }}
                    rewards={recompensaItems}
                    selectableMode={selectableMode}
                    selectionOptions={selectionOptions}
                    onConfirmSelection={handleConfirmSelection}
                    currentAttempts={pendingSelectionItem?.intents ?? pendingSelectionItem?.intentos ?? 0}
                    currentNota={pendingSelectionItem?.nota ?? 100}
                    currentTimeSeconds={pendingSelectionItem?.tiempo_empleado_segundos}
                />
            )}
            {showBattle && (
                <JavaMon
                    userId={user.id + ""}
                    onClose={() => setShowBattle(false)}
                    onSuccess={() => onBattleSuccess()}
                    forcedJavamon={battleJavamon}
                />
            )}
            {rankingsVisible && selectedRecompensaPractica && (
                <PremiosModal
                    idProblema={selectedRecompensaPractica.id + ""}
                    origenTipo="PRACTICA"
                    visible={rankingsVisible}
                    onClose={(refresh: boolean) => {
                        setRankingsVisible(false);
                        if (refresh && selectedRecompensaPractica) {
                            setRecompensesRefreshKeys(prev => ({
                                ...prev,
                                [selectedRecompensaPractica.id]: (prev[selectedRecompensaPractica.id] || 0) + 1
                            }));
                        }
                    }}
                />
            )}

            {modalAssignarRAs && (
                <AssignarRAsMassiuModal
                    visible={modalAssignarRAs}
                    onClose={(updated: boolean) => {
                        setModalAssignarRAs(false);
                        if (updated) {
                            setSelectedRowKeys([]);
                            checkDades();
                        }
                    }}
                    selectedItems={(dades || []).filter((d: any) => selectedRowKeys.includes(d.id))}
                    userId={userId}
                />
            )}
        </>
    );
};

export default Practiques;
